import { createServerFn } from "@tanstack/react-start";
import { asc } from "drizzle-orm";
import z from "zod";
import { db } from "~/db";
import { locationType } from "~/db/schema";

export type LocationType = {
  id: number;
  name: string;
};

const CACHE_TTL_MS = 10 * 60 * 1000; // 10 minutes
let cachedLocationTypes: { data: LocationType[]; timestamp: number } | null =
  null;

const GetLocationTypesSchema = z.object({
  ids: z.array(z.number()).optional(),
  names: z.array(z.string()).optional(),
});

export const getLocationTypes = createServerFn({ method: "GET" })
  .inputValidator(GetLocationTypesSchema.optional())
  .handler(async (ctx) => {
    const ids = ctx.data?.ids;
    const names = ctx.data?.names;

    // Always use cache if available and not expired, then filter client-side if needed
    const now = Date.now();
    if (
      cachedLocationTypes &&
      now - cachedLocationTypes.timestamp < CACHE_TTL_MS
    ) {
      console.log("Serving location types from cache.");
      const types = cachedLocationTypes.data;

      // If names are provided, convert them to IDs first
      let finalIds = ids;
      if (names && names.length > 0) {
        const nameMap = new Map(types.map((type) => [type.name, type.id]));
        finalIds = names
          .map((name) => nameMap.get(name))
          .filter((id): id is number => id !== undefined);
      }

      // If IDs are provided (either directly or converted from names), filter from cache and preserve order
      if (finalIds && finalIds.length > 0) {
        const typeMap = new Map(types.map((type) => [type.id, type]));
        return finalIds
          .map((id) => typeMap.get(id))
          .filter((type): type is LocationType => type !== undefined);
      }
      return types;
    }
    if (
      cachedLocationTypes &&
      now - cachedLocationTypes.timestamp >= CACHE_TTL_MS
    ) {
      cachedLocationTypes = null;
    }

    // If the cache is empty or expired, query the database.
    console.log("Cache miss. Fetching location types from database...");
    const allTypes = await db
      .select({
        id: locationType.id,
        name: locationType.name,
      })
      .from(locationType)
      .groupBy(locationType.id, locationType.name)
      .orderBy(asc(locationType.name)); // Good practice to return them sorted

    // Populate the cache with the data from the database.
    cachedLocationTypes = { data: allTypes, timestamp: Date.now() };

    // If names are provided, convert them to IDs first
    let finalIds = ids;
    if (names && names.length > 0) {
      const nameMap = new Map(
        allTypes.map((type) => [type.name, type.id])
      );
      finalIds = names
        .map((name) => nameMap.get(name))
        .filter((id): id is number => id !== undefined);
    }

    // If IDs are provided (either directly or converted from names), filter the results and preserve order
    if (finalIds && finalIds.length > 0) {
      const typeMap = new Map(
        allTypes.map((type) => [type.id, type])
      );
      return finalIds
        .map((id) => typeMap.get(id))
        .filter((type): type is LocationType => type !== undefined);
    }

    return allTypes;
  });
